import React, { createContext, useState, useEffect, useMemo } from 'react';
// Use the updated utility functions
import { assignPlayersToTeams } from '../utils/teamAssignment'; 
import { 
  createMatchSchedule, 
  createImprovedMatchSchedule,
  createBalancedMatchSchedule,
  validateFieldCapacity,
  updateMatchScore as updateMatchScoreUtil 
} from '../utils/matchAlgorithm';
import { buildTeammateMatrix, buildFieldUsage, summarizeFieldUsage } from '../utils/scheduleMetrics';

const STORAGE_KEY = 'dfb_tournament_state_v2'; // Updated key for new structure
const TOURNAMENT_EXPORT_SCHEMA_VERSION = 1;

// Default palette used for teams (order of preference)
const DEFAULT_TEAM_PALETTE = ['#4CAF50', '#F44336', '#2196F3', '#FF9800', '#FFEB3B', '#E91E63', '#FFFFFF', '#9E9E9E'];

export const TournamentContext = createContext(null);

// Helper function from browser version
const formatTournamentMatrix = (tournamentData, players) => {
  return tournamentData.map((roundTeams, roundIndex) => {
    const playerToTeamMap = {};
    roundTeams.forEach((team, teamIndex) => {
      team.forEach(playerName => {
        playerToTeamMap[playerName] = String.fromCharCode(65 + teamIndex); // A, B, C, etc.
      });
    });
    return {
      round: roundIndex + 1,
      assignments: players.map(playerName => ({
        playerName,
        team: playerToTeamMap[playerName] || '-'
      }))
    };
  });
};

export const TournamentProvider = ({ children }) => {

  // Add polyfill for requestIdleCallback (Safari compatibility)
  if (typeof window !== 'undefined' && !window.requestIdleCallback) {
    window.requestIdleCallback = function(callback, options) {
      const timeout = (options && options.timeout) || 50;
      return setTimeout(callback, timeout);
    };
    window.cancelIdleCallback = function(id) {
      clearTimeout(id);
    };
  }
  
  // Load persisted state
  const persisted = useMemo(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return data;
    } catch (err) {
      return {};
    }
  }, []);

  // State variables aligned with browser version
  const [players, setPlayers] = useState(persisted.players || []);
  const [numFields, setNumFields] = useState(persisted.numFields ?? 1);
  const [numTeams, setNumTeams] = useState(persisted.numTeams ?? 2); // Added numTeams state
  const [numRounds, setNumRounds] = useState(persisted.numRounds ?? 8);
  const [tournamentMatrix, setTournamentMatrix] = useState(persisted.tournamentMatrix || []);
  const [matchSchedule, setMatchSchedule] = useState(persisted.matchSchedule || []);
  const [teamAssignmentHistory, setTeamAssignmentHistory] = useState(persisted.teamAssignmentHistory || []);
  // Manual team sizes (null means auto). Persist across sessions.
  const [manualTeamSizes, setManualTeamSizes] = useState(
    Array.isArray(persisted.manualTeamSizes) ? persisted.manualTeamSizes : []
  );

  // Team colors (array of hex strings). Initialize from persisted or defaults sized to teams.
  const [teamColors, setTeamColors] = useState(() => {
    if (Array.isArray(persisted.teamColors)) {
      return persisted.teamColors.slice(0, Math.max(2, persisted.numTeams || 2));
    }
    // Default: Team A = green (#4CAF50), Team B = red (#F44336), others from palette
    const numTeamsDefault = Math.max(2, persisted.numTeams ?? 2);
    return Array.from({ length: numTeamsDefault }, (_, i) => DEFAULT_TEAM_PALETTE[i % DEFAULT_TEAM_PALETTE.length]);
  });

  // Scoring configuration (points per win, draw, goal scored, goal conceded)
  const DEFAULT_SCORING_CONFIG = { winPoints: 5, drawPoints: 3, goalScoredPoints: 1, goalConcededPoints: 0 };
  const [scoringConfig, setScoringConfig] = useState(() => {
    if (persisted.scoringConfig && typeof persisted.scoringConfig === 'object') {
      return { ...DEFAULT_SCORING_CONFIG, ...persisted.scoringConfig };
    }
    return DEFAULT_SCORING_CONFIG;
  });

  // Compute which rounds are completed (all scores entered) - derived from matchSchedule
  const completedRounds = useMemo(() => {
    if (!matchSchedule || matchSchedule.length === 0) return [];
    return matchSchedule.map(roundMatches => {
      // A round is complete if ALL matches have BOTH scores entered as actual numbers
      return roundMatches.every(match =>
        typeof match.scoreA === 'number' && typeof match.scoreB === 'number'
      );
    });
  }, [matchSchedule]);

  // Optimizer progress
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState(0);
  // Removed playersPerTeam, excludedPlayers, playerHistory as they are not in the browser version logic being copied

  // Autosave effect
  useEffect(() => {
    const id = window.requestIdleCallback(() => {
      const data = {
        players,
        numFields,
        numTeams,
        numRounds,
        tournamentMatrix,
        matchSchedule,
        teamAssignmentHistory,
        manualTeamSizes,
        teamColors,
        scoringConfig,
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        // Handle quota exceeded error
        if (error.name === 'QuotaExceededError') {
          // Try to save minimal state (just players and settings, no tournament data)
          try {
            const minimalData = {
              players,
              numFields,
              numTeams,
              numRounds,
              manualTeamSizes,
              teamColors,
              scoringConfig,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(minimalData));
          } catch (minimalError) {
            // Even minimal save failed - clear storage and warn user
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      }
    }, { timeout: 1000 });
    return () => window.cancelIdleCallback(id);
  }, [players, numFields, numTeams, numRounds, tournamentMatrix, matchSchedule, teamAssignmentHistory, manualTeamSizes, teamColors, scoringConfig]);

  // Before-unload guard
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // --- Action Functions --- 

  const addPlayer = (playerName) => {
    const trimmed = playerName.trim();
    setPlayers(prev => {
      if (trimmed && prev.length < 100 && !prev.includes(trimmed)) {
        return [...prev, trimmed];
      }
      return prev;
    });
  };

  // Bulk import players
  const importPlayers = (names) => {
    setPlayers(prev => {
      const newList = [...prev];
      names.forEach(name => {
        const trimmed = name.trim();
        if (trimmed && newList.length < 100 && !newList.includes(trimmed)) {
          newList.push(trimmed);
        }
      });
      return newList;
    });
  };

  // Export players to CSV
  const exportPlayers = () => {
    const header = 'Spielername\n';
    const csvContent = players.reduce((acc, name) => acc + `${name}\n`, header);
    // Add UTF-8 BOM to ensure proper encoding for German characters
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'players_export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removePlayer = (playerIndex) => {
    // Check if index is valid before splicing
    if (playerIndex >= 0 && playerIndex < players.length) {
      const updatedPlayers = [...players];
      updatedPlayers.splice(playerIndex, 1);
      setPlayers(updatedPlayers);
      // Consider resetting tournament if a player is removed?
      // setTournamentMatrix([]);
      // setMatchSchedule([]);
      // setTeamAssignmentHistory([]);
    }
  };

  // Rename a player and propagate across structures
  const renamePlayer = (playerIndex, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed || playerIndex < 0 || playerIndex >= players.length) return;
    const oldName = players[playerIndex];
    if (oldName === trimmed) return;
    // Prevent duplicate names
    if (players.includes(trimmed)) return;
    // Update players list
    const updatedPlayers = [...players];
    updatedPlayers[playerIndex] = trimmed;
    setPlayers(updatedPlayers);
    // Update teamAssignmentHistory (raw team arrays contain player names)
    if (teamAssignmentHistory && teamAssignmentHistory.length) {
      const updatedHistory = teamAssignmentHistory.map(round => round.map(team => team.map(p => p === oldName ? trimmed : p)));
      setTeamAssignmentHistory(updatedHistory);
    }
    // Update tournamentMatrix (assignments array with playerName + team)
    if (tournamentMatrix && tournamentMatrix.length) {
      const updatedMatrix = tournamentMatrix.map(roundObj => ({
        ...roundObj,
        assignments: roundObj.assignments.map(a => a.playerName === oldName ? { ...a, playerName: trimmed } : a)
      }));
      setTournamentMatrix(updatedMatrix);
    }
    // matchSchedule refers to teams by index, so no change needed there
  };

  const updateFields = (fields) => {
    const fieldsNum = parseInt(fields, 10);
    if (!isNaN(fieldsNum) && fieldsNum >= 1) { // Ensure at least 1 field
      setNumFields(fieldsNum);
    }
  };

  const updateTeams = (teams) => {
    const teamsNum = parseInt(teams, 10);
    // Ensure at least 2 teams and not more than players
    if (!isNaN(teamsNum) && teamsNum >= 2 && teamsNum <= players.length) { 
      setNumTeams(teamsNum);
      // keep manual sizes array in sync with number of teams
      setManualTeamSizes(prev => Array.from({ length: teamsNum }, (_, i) => (typeof prev[i] !== 'undefined' ? prev[i] : null)));
      // keep teamColors in sync, fill with defaults if needed
      // IMPORTANT: preserve explicit null values (transparent). Use typeof check instead of ?? so null is kept.
      setTeamColors(prev => Array.from({ length: teamsNum }, (_, i) => (typeof prev[i] !== 'undefined' ? prev[i] : DEFAULT_TEAM_PALETTE[i % DEFAULT_TEAM_PALETTE.length])));
    }
  };

  const updateRounds = (rounds) => {
    const roundsNum = parseInt(rounds, 10);
    if (!isNaN(roundsNum) && roundsNum >= 1) { // Ensure at least 1 round
      setNumRounds(roundsNum);
    }
  };

  // Reset state and clear storage
  const resetAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPlayers([]);
    setNumFields(1);
    setNumTeams(2);
    setNumRounds(8);
    setTournamentMatrix([]);
    setMatchSchedule([]);
    setTeamAssignmentHistory([]);
    setManualTeamSizes([]);
    // Reset team colors to defaults (green for A, red for B)
    setTeamColors([DEFAULT_TEAM_PALETTE[0], DEFAULT_TEAM_PALETTE[1]]);
    // Reset scoring config to defaults
    setScoringConfig({ winPoints: 5, drawPoints: 3, goalScoredPoints: 1, goalConcededPoints: 0 });
  };

  // Update scoring configuration
  const updateScoringConfig = (newConfig) => {
    setScoringConfig(prev => ({ ...prev, ...newConfig }));
  };

  // --- Tournament Export / Import ---
  const exportTournament = () => {
    const payload = {
      type: 'dfb-changing-teams-tournament',
      schemaVersion: TOURNAMENT_EXPORT_SCHEMA_VERSION,
      appVersion: typeof process !== 'undefined' ? (process.env?.npm_package_version || 'unknown') : 'unknown',
      generatedAt: new Date().toISOString(),
      data: {
        players,
        numFields,
        numTeams,
        numRounds,
        teamAssignmentHistory,
        tournamentMatrix,
        matchSchedule,
        // include teamColors so imports can restore visual state
        teamColors,
      }
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tournament_export_${new Date().toISOString().replace(/[:.]/g,'-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const importTournament = (file, { confirmOverwrite = true } = {}) => {
    if (!file) return Promise.reject(new Error('No file provided'));
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.onload = () => {
        try {
          const json = JSON.parse(reader.result);
          if (json.type !== 'dfb-changing-teams-tournament') {
            throw new Error('Ungültiger Dateityp');
          }
          if (json.schemaVersion > TOURNAMENT_EXPORT_SCHEMA_VERSION) {
            throw new Error('Export-Version neuer als unterstützte Version');
          }
          const d = json.data || {};
          const required = ['players','numFields','numTeams','numRounds','teamAssignmentHistory','tournamentMatrix','matchSchedule'];
            for (const key of required) {
              if (d[key] === undefined) throw new Error(`Feld fehlt: ${key}`);
            }
          if (confirmOverwrite && (tournamentMatrix.length || matchSchedule.length || players.length)) {
            if (!window.confirm('Aktuelles Turnier überschreiben?')) {
              resolve(false);
              return;
            }
          }
          // Apply core state
          setPlayers(d.players || []);
          setNumFields(d.numFields ?? 1);
          const teamsFromData = d.numTeams ?? 2;
          setNumTeams(teamsFromData);
          setNumRounds(d.numRounds ?? 1);
          setTeamAssignmentHistory(Array.isArray(d.teamAssignmentHistory) ? d.teamAssignmentHistory : []);
          setTournamentMatrix(Array.isArray(d.tournamentMatrix) ? d.tournamentMatrix : []);
          setMatchSchedule(Array.isArray(d.matchSchedule) ? d.matchSchedule : []);

          // If the import contains teamColors, apply them immediately.
          // Build a teamColors array of length teamsFromData, preserving explicit nulls and falling back to defaults for missing entries.
          if (Array.isArray(d.teamColors)) {
            const nextColors = Array.from({ length: teamsFromData }, (_, i) => (typeof d.teamColors[i] !== 'undefined' ? d.teamColors[i] : DEFAULT_TEAM_PALETTE[i % DEFAULT_TEAM_PALETTE.length]));
            setTeamColors(nextColors);
          } else {
            // Ensure teamColors is at least in sync with team count
            setTeamColors(prev => Array.from({ length: teamsFromData }, (_, i) => (typeof prev[i] !== 'undefined' ? prev[i] : DEFAULT_TEAM_PALETTE[i % DEFAULT_TEAM_PALETTE.length])));
          }

          resolve(true);
        } catch (e) {
          reject(e);
        }
      };
      reader.readAsText(file, 'utf-8');
    });
  };

  // generateTournament function from browser version
  const generateTournament = async (useImprovedAlgorithm = false, overrideTeams = null, overrideFields = null, options = {}) => {
    // Use override values if provided, otherwise use current state
    const teamsToUse = overrideTeams !== null ? overrideTeams : numTeams;
    const fieldsToUse = overrideFields !== null ? overrideFields : numFields;

    if (players.length === 0 || teamsToUse <= 0 || numRounds <= 0) {
      return false;
    }

    // Confirmation check from browser version (slightly modified)
    if (tournamentMatrix.length > 0 || matchSchedule.length > 0) {
       if (!window.confirm('Dies überschreibt das aktuelle Turnier und alle Ergebnisse. Sind Sie sicher?')) {
         return false;
       }
    }

    const effectiveSizes = getEffectiveTeamSizes();

    const scoreTournament = (assignments, schedule) => {
      // teammate repeat score
      const display = formatTournamentMatrix(assignments, players);
      const { matrix } = buildTeammateMatrix(players, display);
      let teammatePenalty = 0;
      for (let i = 0; i < matrix.length; i++) {
        for (let j = i + 1; j < matrix.length; j++) {
          const c = matrix[i][j];
          teammatePenalty += c * c;
        }
      }
      // opponent repeat on team indices
      let opponentPenalty = 0;
      const seen = new Map();
      const key = (a,b) => (a < b ? `${a}-${b}` : `${b}-${a}`);
      schedule.forEach(round => round.forEach(m => {
        const k = key(m.teamA, m.teamB);
        seen.set(k, (seen.get(k) || 0) + 1);
      }));
      for (const cnt of seen.values()) if (cnt > 1) opponentPenalty += (cnt - 1);

      // field balance
      const getTeamField = (roundIdx, teamLetter) => {
        const teamIndex = teamLetter.charCodeAt(0) - 65;
        const match = schedule[roundIdx]?.find(m => m.teamA === teamIndex || m.teamB === teamIndex);
        return match ? match.field : null;
      };
      const usage = buildFieldUsage(players, display, getTeamField);
      const summary = summarizeFieldUsage(usage);
      const fieldPenalty = summary.perPlayer.reduce((acc, p) => acc + p.spread, 0);

      return teammatePenalty * 10 + opponentPenalty * 3 + fieldPenalty * 1;
    };

  const MAX_ATTEMPTS = Number.isInteger(options.maxAttempts) && options.maxAttempts > 0 ? options.maxAttempts : 250;
    setOptimizing(true);
    setOptimizeProgress(0);
    let bestScore = Infinity;
    let bestAssignments = null;
    let bestSchedule = null;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const assignments = [];
      for (let r = 0; r < numRounds; r++) {
        const roundTeams = assignPlayersToTeams(players, teamsToUse, assignments, effectiveSizes);
        assignments.push(roundTeams);
      }
      const schedule = createMatchSchedule(assignments, fieldsToUse);
      const s = scoreTournament(assignments, schedule);
      if (s < bestScore) {
        bestScore = s;
        bestAssignments = assignments;
        bestSchedule = schedule;
      }
      setOptimizeProgress((attempt + 1) / MAX_ATTEMPTS);
      if (attempt % 5 === 0) {
        await new Promise(res => setTimeout(res, 0));
      }
    }

    // Commit best
    setTeamAssignmentHistory(bestAssignments);
    const formattedMatrix = formatTournamentMatrix(bestAssignments, players);
    setTournamentMatrix(formattedMatrix);
    setMatchSchedule(bestSchedule);
    setOptimizing(false);
    setOptimizeProgress(1);
    return true;
  };

  // updateScore function using the utility
  const updateScore = (roundIndex, matchIndex, scoreA, scoreB) => {
    if (roundIndex < 0 || roundIndex >= matchSchedule.length) return;
    if (matchIndex < 0 || matchIndex >= matchSchedule[roundIndex].length) return;

    const newSchedule = [...matchSchedule];
    // Ensure the specific round exists and is an array
    if (!Array.isArray(newSchedule[roundIndex])) {
        return;
    }
    // Ensure the specific match exists
    if (!newSchedule[roundIndex][matchIndex]) {
        return;
    }

    const newMatch = updateMatchScoreUtil(
      newSchedule[roundIndex][matchIndex],
      scoreA,
      scoreB
    );

    newSchedule[roundIndex][matchIndex] = newMatch;
    setMatchSchedule(newSchedule);
  };

  // getTeamName remains the same
  const getTeamName = (teamIndex) => {
    return String.fromCharCode(65 + teamIndex); // A, B, C, etc.
  };
  
  // getTeamPlayers uses teamAssignmentHistory now
  const getTeamPlayers = (roundIndex, teamIndex) => {
    // Add checks for valid indices
    if (!teamAssignmentHistory || roundIndex < 0 || roundIndex >= teamAssignmentHistory.length) {
      return [];
    }
    const round = teamAssignmentHistory[roundIndex];
    if (!round || teamIndex < 0 || teamIndex >= round.length) {
      return [];
    }
    return round[teamIndex] || []; // Return empty array if team doesn't exist
  };

  // ---------------- Manual team size controls ----------------
  const getEffectiveTeamSizes = () => {
    const sizes = Array.from({ length: numTeams }, () => null);
    let sumManual = 0;
    let manualCount = 0;
    for (let i = 0; i < numTeams; i++) {
      const v = manualTeamSizes[i];
      if (Number.isInteger(v) && v >= 0) {
        sizes[i] = v;
        sumManual += v;
        manualCount++;
      }
    }
    const remainingTeams = numTeams - manualCount;
    let remainingPlayers = Math.max(0, players.length - sumManual);
    if (remainingTeams > 0) {
      const base = Math.floor(remainingPlayers / remainingTeams);
      let remainder = remainingPlayers % remainingTeams;
      for (let i = 0; i < numTeams; i++) {
        if (sizes[i] == null) {
          sizes[i] = base + (remainder > 0 ? 1 : 0);
          if (remainder > 0) remainder--;
        }
      }
    } else {
      // If all teams are manual, fill nulls with 0
      for (let i = 0; i < numTeams; i++) sizes[i] = sizes[i] ?? 0;
    }
    return sizes;
  };

  const getPairMate = (teamIndex) => (teamIndex % 2 === 0 ? teamIndex + 1 : teamIndex - 1);

  const getPairStart = (teamIndex) => teamIndex - (teamIndex % 2);

  const canAdjustPairSize = (pairIndex, delta) => {
    const start = getPairStart(pairIndex);
    const mate = start + 1;
    if (start < 0 || mate >= numTeams) return false;
    const sizes = getEffectiveTeamSizes();
    const selfVal = (manualTeamSizes[start] ?? sizes[start]);
    const mateVal = (manualTeamSizes[mate] ?? sizes[mate]);
    const currentPairVal = Math.floor((selfVal + mateVal) / 2);
    const target = currentPairVal + delta;
    if (target < 0) return false;
    // Check total does not exceed players when both teams take the same value
    let sumOthersManual = 0;
    for (let i = 0; i < numTeams; i++) {
      if (i === start || i === mate) continue;
      const v = manualTeamSizes[i];
      if (Number.isInteger(v) && v >= 0) sumOthersManual += v;
    }
    const pairSum = target * 2;
    return sumOthersManual + pairSum <= players.length;
  };

  const adjustPairSize = (pairIndex, delta) => {
    if (!canAdjustPairSize(pairIndex, delta)) return;
    const start = getPairStart(pairIndex);
    const mate = start + 1;
    const sizes = getEffectiveTeamSizes();
    const selfVal = (manualTeamSizes[start] ?? sizes[start]);
    const mateVal = (manualTeamSizes[mate] ?? sizes[mate]);
    const currentPairVal = Math.floor((selfVal + mateVal) / 2);
    const target = currentPairVal + delta;
    setManualTeamSizes(prev => {
      const next = Array.from({ length: numTeams }, (_, i) => prev[i] ?? null);
      next[start] = target;
      next[mate] = target;
      return next;
    });
  };

  const resetManualPair = (teamIndex) => {
    const mate = getPairMate(teamIndex);
    setManualTeamSizes(prev => {
      const next = [...prev];
      if (teamIndex >= 0 && teamIndex < numTeams) next[teamIndex] = null;
      if (mate >= 0 && mate < numTeams) next[mate] = null;
      return next;
    });
  };

  // Field capacity validation
  const getFieldValidation = () => {
    return validateFieldCapacity(numTeams, numFields, numRounds);
  };

  // isValidConfiguration check from browser version
  const isValidConfiguration = () => {
    const valid = players.length > 0 && numTeams >= 2 && numRounds >= 1 && numFields >= 1 && players.length >= numTeams;
    return valid;
  };

  // update or set a team's color
  const updateTeamColor = (teamIndex, color) => {
    if (teamIndex < 0 || teamIndex >= numTeams) return;
    setTeamColors(prev => {
      // Preserve explicit nulls. Initialize missing entries with defaults.
      const length = Math.max(prev.length, numTeams);
      const next = Array.from({ length }, (_, i) => (typeof prev[i] !== 'undefined' ? prev[i] : DEFAULT_TEAM_PALETTE[i % DEFAULT_TEAM_PALETTE.length]));
      next[teamIndex] = color; // color may be null to represent transparent
      return next;
    });
  };

  // Context value provided to consumers
  const value = {
    players,
    addPlayer,
    renamePlayer,
    importPlayers,
    exportPlayers,
    exportTournament,
    importTournament,
    removePlayer,
    numFields,
    updateFields, // Renamed from setNumFields for consistency
    numTeams,     // Added numTeams
    updateTeams,  // Added updateTeams
    numRounds,
    updateRounds, // Renamed from setNumRounds
    tournamentMatrix, // Formatted matrix for display
    matchSchedule,
    teamAssignmentHistory, // Raw assignments for logic
    generateTournament,
    updateScore,
    getTeamName,
    getTeamPlayers,
    getFieldValidation,
    manualTeamSizes,
    getEffectiveTeamSizes,
    canAdjustPairSize,
    adjustPairSize,
    resetManualPair,
    optimizing,
    optimizeProgress,
    isValidConfiguration,
    resetAll,
    // New color-related exports
    teamColors,
    updateTeamColor,
    // Round completion tracking (computed from scores)
    completedRounds,
    // Scoring configuration
    scoringConfig,
    updateScoringConfig,
  };

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  );
};